import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import {
  EditOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { message, Spin } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import AppContext from "../../../context/AppContext";
import { AppStrings } from "../../../utils/constants/AppStrings";
import {
  GAME_PASS_DROPDOWN_LABELS,
  SIDER_TABS,
} from "../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { usePamFeature } from "../../../hooks/useFeatureFlag";
import { Button, Modal, ToggleSwitch } from "../../shared";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import EndSeasonConfirmModal from "./EndSeasonConfirmModal/EndSeasonConfirmModal";
import ChallengeCard from "../CreateSeason/ChallengesAndTasks/ChallengeCard";
import ChallengesAndTasks from "../CreateSeason/ChallengesAndTasks/ChallengesAndTasks";
import Schedule from "../CreateSeason/Schedule/Schedule";
import type {
  BetCurrency,
  BetType,
  CasinoAction,
  ChallengeDraft,
  ChallengesValues,
  RewardDraft,
  RewardType,
  ScheduleValues,
  SocialTaskType,
  SportOption,
  TaskDraft,
  TaskQualifier,
  TaskType,
} from "../CreateSeason/createSeasonData";
import {
  deriveSameXpReward,
  isScheduleValid,
  mapProgressionModeToSequential,
  MONEY_TYPE_TO_BET_CURRENCY,
  resolveAutoStartAnchor,
  CASINO_TASK_SUBMISSION_MODE,
  resolveTaskSubmissionModeFromApiTask,
  sortTasksByChallengeOrder,
  type TaskSubmissionModeDefaults,
} from "../CreateSeason/createSeasonData";
import {
  useActivateSeason,
  type ActivateSeasonVariables,
} from "../../../queries/game-pass/useActivateSeason";
import { useCancelSeason } from "../../../queries/game-pass/useCancelSeason";
import { useUnlockSeason } from "../../../queries/game-pass/useUnlockSeason";
import { useGetSeasonsList } from "../../../queries/game-pass/useGetSeasonsList";
import { useGetChallengesList } from "../../../queries/game-pass/useGetChallengesList";
import { useGetTasksList } from "../../../queries/game-pass/useGetTasksList";
import { buildCasinoGameLabelsFromTaskGames } from "../../../queries/game-pass/casinoGameNormalizers";
import { useDeleteSeason } from "../../../queries/game-pass/useDeleteSeason";
import { useEndSeason } from "../../../queries/game-pass/useEndSeason";
import { useScheduleSeason } from "../../../queries/game-pass/useScheduleSeason";
import { useUnscheduleSeason } from "../../../queries/game-pass/useUnscheduleSeason";
import {
  mapEditSeasonValuesToUpdateSeasonRequest,
  useUpdateSeason,
} from "../../../queries/game-pass/useUpdateSeason";
import {
  buildSeasonScheduleConflictMessage,
  findSeasonScheduleConflict,
} from "../../../queries/game-pass/seasonScheduleConflict";
import {
  buildSeasonScheduleReadinessMessage,
  findSeasonScheduleReadinessIssue,
} from "../../../queries/game-pass/seasonScheduleReadiness";
import { useGetTaskOptions } from "../../../queries/game-pass/useGetTaskOptions";
import { getSportOptions } from "../../../queries/game-pass/gamePassOptions";
import {
  mapApiSeasonToSeasonData,
  seasonStatusToBucket,
} from "../../../queries/game-pass/seasonsMappers";
import { CHALLENGE_CURRENCY_TO_REWARD_TYPE } from "../../../queries/game-pass/challengeRewards";
import {
  getApiErrorMessage,
  shouldOfferForceActivate,
} from "../../../utils/ErrorUtils";
import { resolveGamePassImgKeyForSave } from "../../../utils/gamePassImageUpload";
import {
  findSeasonDetailsById,
  parseFilterId,
  SEASONS_FILTER_QUERY_PARAM,
  type SeasonDetailsData,
} from "../SeasonsList/seasonsListData";
import {
  SEASON_API_STATUS,
  SEASON_LIFECYCLE_BUCKET,
  TASK_BET_TYPE,
  TASK_CASINO_ACTION,
  TASK_DATA_SOURCE,
  TASK_OUTCOME_REQUIRED,
  TASK_RULE_FIELD,
  TASK_RULE_OP,
  type ChallengeApiRecord,
  type ChallengeRewardTier,
  type CreateChallengeRewardRequest,
  type EndSeasonAudit,
  type SeasonApiRecord,
  type SeasonTaskSubmissionMode,
  type TaskListRecord,
  type TaskRule,
} from "../../../types/gamePass";

import EditSeasonModal, {
  type EditSeasonValues,
} from "./EditSeasonModal/EditSeasonModal";

import styles from "./SeasonDetails.module.css";

dayjs.extend(utc);

type AppContextValue = {
  setActiveSidebarTab: (tabName: string, dropdownOption?: string | false) => void;
  adminReadOnly?: boolean;
};

/** Per-Figma audience metric tile inside the General card. Kept as a small
 *  internal component so the three tiles render identically and the link
 *  variant (`onSeeAll`) reads as a clear branch instead of an inline
 *  ternary inside the JSX. */
type MetricTileProps = {
  label: string;
  /** Pre-formatted value (e.g. "2,012", "68%"). Optional — when omitted,
   *  the tile renders just the See-All link (matches the All Reports
   *  tile in the Figma). */
  value?: string;
  onSeeAll?: () => void;
  testId: string;
};

const MetricTile: FC<MetricTileProps> = ({ label, value, onSeeAll, testId }) => (
  <div className={styles.generalTile} data-testid={testId}>
    <span className={styles.metricLabel}>{label}</span>
    {value !== undefined && <span className={styles.metricValue}>{value}</span>}
    {onSeeAll && (
      <button
        type="button"
        className={styles.metricLink}
        onClick={onSeeAll}
        data-testid={`${testId}-see-all`}
      >
        <span>{AppStrings.SEASON_DETAILS_METRIC_SEE_ALL}</span>
        <RightOutlined className={styles.metricLinkIcon} aria-hidden="true" />
      </button>
    )}
  </div>
);

/**
 * Builds the initial `EditSeasonValues` snapshot from a resolved
 * `SeasonDetailsData`. Pure and free of component-scoped state, so it
 * lives at module level — wrapping it in `useCallback` only added noise
 * (the empty dep array proved there was nothing to memoise against).
 *
 * Returns sensible defaults when `source` is `undefined` so the modal's
 * controlled-output contract is never seeded with `undefined` fields.
 */
const seedEditableGeneral = (
  source: SeasonDetailsData | undefined,
): EditSeasonValues => ({
  name: source?.season.name ?? "",
  description: source?.general.description ?? "",
  tiers: source?.general.tiers ?? { free: true, premium: true },
  sportsbookSubmissionMode: source?.general.sportsbookSubmissionMode ?? "AUTO",
  casinoSubmissionMode: source?.general.casinoSubmissionMode ?? "AUTO",
  imgKey: source?.general.imgKey,
  imageUrl: source?.general.imageUrl,
  schedule:
    source?.general.schedule ?? {
      autoStartAfter: false,
    },
});

/** Upload a freshly picked banner (or honour an explicit clear) before the
 *  season update mapper runs. Throws on upload failure so the caller can keep
 *  the modal open and surface the error — same contract as challenge / task. */
const stampSeasonBannerForSave = async (
  next: EditSeasonValues,
): Promise<EditSeasonValues> => {
  const resolvedImgKey = await resolveGamePassImgKeyForSave({
    file: next.imageFile,
    persistedKey: next.imgKey,
    folder: "seasons",
  });
  if (resolvedImgKey === undefined) {
    return { ...next, imageFile: undefined };
  }
  return {
    ...next,
    imageFile: undefined,
    imgKey: resolvedImgKey,
    imageUrl: resolvedImgKey === null ? null : next.imageUrl,
  };
};

const formatFullDateTimeRange = (
  start?: string,
  end?: string,
): string => {
  // Stored UTC → admin's local zone so the displayed range matches the
  // wall-clock the Schedule pickers show.
  const startDay = start ? dayjs.utc(start).local() : null;
  const endDay = end ? dayjs.utc(end).local() : null;
  if (!startDay?.isValid() || !endDay?.isValid()) {
    return `${start ?? ""} - ${end ?? ""}`;
  }
  return `${startDay.format("MMMM D, YYYY (h:mma)")} - ${endDay.format("MMMM D, YYYY (h:mma)")}`;
};

const mapChallengeCurrencyToRewardType = (
  currency?: string | null,
): RewardType | undefined => {
  if (currency === "value" || currency === "play") {
    return CHALLENGE_CURRENCY_TO_REWARD_TYPE[currency];
  }
  return undefined;
};

/** Persisted bonus params come back typed (numbers); the editor keeps them as
 *  strings, so coerce each back for the inputs. */
const stringifyRewardParams = (
  params?: CreateChallengeRewardRequest["params"],
): Record<string, string> | undefined => {
  if (!params || typeof params !== "object") return undefined;
  const stringified: Record<string, string> = {};
  for (const [name, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    stringified[name] = String(value);
  }
  return Object.keys(stringified).length > 0 ? stringified : undefined;
};

/** Re-hydrate a persisted challenge reward into an editor row. A reward that
 *  carries a `reward_type` is a bonus reward — round-trip its grant id,
 *  currency and params so the picker re-selects it and its param inputs
 *  re-populate. Otherwise it's a play/value currency reward and collapses back
 *  to the canonical Rebet type for its currency. */
const mapApiRewardToDraft = (
  reward: CreateChallengeRewardRequest,
  idBase: string,
  index: number,
): RewardDraft => {
  const id = `${idBase}-reward-${index}`;
  const rewardType =
    typeof reward.reward_type === "string" ? reward.reward_type.trim() : "";
  if (rewardType) {
    return {
      id,
      amount: String(reward.amount),
      type: rewardType,
      bonusRewardType: rewardType,
      bonusCurrency:
        typeof reward.currency === "string" ? reward.currency : undefined,
      params: stringifyRewardParams(reward.params),
    };
  }
  return {
    id,
    amount: String(reward.amount),
    type: mapChallengeCurrencyToRewardType(reward.currency),
  };
};

/** Coerce the BE `data_source` string to the modal's `TaskType` pill set,
 *  defaulting to Sportsbook for anything unrecognised (or null). The BE's
 *  social value is the singular `"SOCIAL"` (`TASK_DATA_SOURCE.SOCIAL`) — it
 *  maps onto the plural `"SOCIALS"` pill value the modal uses. */
const mapTaskDataSourceToType = (
  dataSource?: string | null,
): TaskType => {
  if (dataSource === TASK_DATA_SOURCE.SOCIAL) return "SOCIALS";
  if (dataSource === TASK_DATA_SOURCE.CASINO) return "CASINO";
  return "SPORTSBOOK";
};

/** Coerce the BE `bet_type` to the modal's `BetType` pill set. A null /
 *  unknown value collapses to the `"ANY"` UI sentinel (the BE's "no
 *  restriction" state). */
const mapTaskBetType = (betType?: string | null): BetType => {
  if (
    betType === TASK_BET_TYPE.SINGLE ||
    betType === TASK_BET_TYPE.PARLAY ||
    betType === TASK_BET_TYPE.SGP ||
    betType === TASK_BET_TYPE.LIVE
  ) {
    return betType;
  }
  return "ANY";
};

/** Coerce the BE `outcome_required` to the modal's `TaskQualifier`. Returns
 *  `undefined` (the "not yet picked" sentinel) when absent or unrecognised. */
const mapTaskQualifier = (
  outcome?: string | null,
): TaskQualifier | undefined => {
  if (
    outcome === TASK_OUTCOME_REQUIRED.WIN ||
    outcome === TASK_OUTCOME_REQUIRED.WIN_OR_PUSH ||
    outcome === TASK_OUTCOME_REQUIRED.ANY_SETTLED
  ) {
    return outcome;
  }
  return undefined;
};

/** Coerce the BE `outcome` rule value to the modal's `CasinoAction` pill set.
 *  Returns `undefined` when absent / unrecognised so the modal falls back to
 *  its default Play pill. Accepts the legacy `"PLAY"` wire value for tasks
 *  persisted before FE-2005, and lowercase `"win"` (the BE normalises Win to
 *  lowercase on read even though the write path accepts `"WIN"`). */
const mapTaskCasinoAction = (
  action?: string | null,
): CasinoAction | undefined => {
  if (action === TASK_CASINO_ACTION.PLAY || action === "PLAY") return "PLAY";
  if (
    action === TASK_CASINO_ACTION.WIN ||
    (typeof action === "string" && action.toLowerCase() === "win")
  ) {
    return "WIN";
  }
  return undefined;
};

/** Pull the id list for a given rule `field` out of a task's `rules` array
 *  (Game Pass V2). Handles both `op: "eq"` (scalar value) and `op: "in"`
 *  (array value). Returns `undefined` when no matching rule exists so the
 *  caller can fall back to the legacy top-level column. */
const extractRuleIds = (
  rules: ReadonlyArray<TaskRule> | null | undefined,
  field: string,
): string[] | undefined => {
  if (!Array.isArray(rules)) return undefined;
  const rule = rules.find((entry) => entry?.field === field);
  if (!rule) return undefined;
  const { value } = rule;
  if (Array.isArray(value)) {
    const ids = value.filter((id): id is string => typeof id === "string");
    return ids.length > 0 ? ids : undefined;
  }
  return typeof value === "string" && value !== "" ? [value] : undefined;
};

/** Resolve a selection that may arrive either as a Game Pass V2 rule or as a
 *  legacy top-level array column. Rules win; the legacy field is a fallback
 *  for tasks persisted before the rules migration (and for any BE that still
 *  echoes the old columns). `undefined` when neither carries a selection. */
const resolveSelectionFromRules = (
  rules: ReadonlyArray<TaskRule> | null | undefined,
  field: string,
  legacy: ReadonlyArray<string> | null | undefined,
): string[] | undefined => {
  const ruleIds = extractRuleIds(rules, field);
  if (ruleIds && ruleIds.length > 0) return ruleIds;
  return Array.isArray(legacy) && legacy.length > 0 ? [...legacy] : undefined;
};

/** Pull a scalar (non-array) rule `value` for a given `field` out of a task's
 *  `rules` array. Used for the `outcome` / `bet_amount` rules, whose value is
 *  a single string or number. `undefined` when no matching rule exists. */
const extractScalarRuleValue = (
  rules: ReadonlyArray<TaskRule> | null | undefined,
  field: string,
  /** When supplied, also match the rule's operator. Needed for a field that is
   *  carried by more than one rule — e.g. `odds_american` ships both a `gte`
   *  (Minimum Odds) and an `lte` (Maximum Odds) bound, so field alone is
   *  ambiguous. Omit to match the first rule on `field` regardless of op. */
  op?: string,
): string | number | undefined => {
  if (!Array.isArray(rules)) return undefined;
  const rule = rules.find(
    (entry) =>
      entry?.field === field && (op === undefined || entry?.op === op),
  );
  if (!rule) return undefined;
  const { value } = rule;
  return typeof value === "string" || typeof value === "number"
    ? value
    : undefined;
};

/** Resolve the Casino Action pill (Play / Win) from the `outcome` rule
 *  (Game Pass V2). `undefined` when absent / unrecognised so the modal falls
 *  back to its default Play pill. */
const resolveCasinoActionFromRules = (
  rules: ReadonlyArray<TaskRule> | null | undefined,
): CasinoAction | undefined => {
  const outcomeValue = extractScalarRuleValue(rules, TASK_RULE_FIELD.OUTCOME);
  return mapTaskCasinoAction(
    typeof outcomeValue === "string" ? outcomeValue : undefined,
  );
};

/** Resolve the casino Bet Currency multi-select from the `money_type` rule
 *  (Game Pass V2). Maps each wire value (`rebet_cash` / `rebet_coin`) back to
 *  the UI Bet Currency pill; `undefined` when no recognised value is present so
 *  the select renders empty. */
const resolveCasinoBetCurrenciesFromRules = (
  rules: ReadonlyArray<TaskRule> | null | undefined,
): ReadonlyArray<BetCurrency> | undefined => {
  const ids = extractRuleIds(rules, TASK_RULE_FIELD.MONEY_TYPE);
  if (!ids || ids.length === 0) return undefined;
  const currencies = ids
    .map((id) => MONEY_TYPE_TO_BET_CURRENCY[id])
    .filter((currency): currency is BetCurrency => currency !== undefined);
  return currencies.length > 0 ? currencies : undefined;
};

/** Resolve a numeric free-text trigger value (Action Amount / Minimum Bet
 *  Amount) from a `gte` rule, falling back to a legacy top-level numeric
 *  column. Returns the modal's string representation, or `undefined` when
 *  neither carries a value. */
const resolveNumericFromRules = (
  rules: ReadonlyArray<TaskRule> | null | undefined,
  field: string,
  legacy?: number | null,
  /** Optional operator to disambiguate a field carried by multiple rules
   *  (e.g. `odds_american` gte vs lte). Forwarded to `extractScalarRuleValue`. */
  op?: string,
): string | undefined => {
  const ruleValue = extractScalarRuleValue(rules, field, op);
  if (typeof ruleValue === "number" && Number.isFinite(ruleValue))
    return String(ruleValue);
  if (typeof ruleValue === "string" && ruleValue.trim() !== "")
    return ruleValue;
  return legacy !== null && legacy !== undefined ? String(legacy) : undefined;
};

/** Reconstruct the Socials "Task Type" dropdown value from a social task's
 *  `rules` array (Game Pass V2), the inverse of `buildSocialRules`:
 *    - `is_new_relationship`       → `FOLLOW_X_FOLLOWERS`
 *    - `opponent_account_age_days` → `P2P_WINS`
 *    - `shared_device_flag`        → `CONSECUTIVE_PICK_DAYS`
 *    - `consecutive_days`          → `CONSECUTIVE_LOGIN_DAYS`
 *  The login and pick streaks both carry a `consecutive_days` rule, so the
 *  pick check (`shared_device_flag`, the marker only the pick variant emits)
 *  MUST run before the `consecutive_days` check — otherwise a pick task would
 *  resolve to the login streak. Returns `undefined` when no recognised social
 *  rule is present so the modal falls back to the default option. */
const resolveSocialTaskTypeFromRules = (
  rules: ReadonlyArray<TaskRule> | null | undefined,
): SocialTaskType | undefined => {
  if (!Array.isArray(rules)) return undefined;
  const hasField = (field: string): boolean =>
    rules.some((rule) => rule?.field === field);
  if (hasField(TASK_RULE_FIELD.IS_NEW_RELATIONSHIP)) return "FOLLOW_X_FOLLOWERS";
  if (hasField(TASK_RULE_FIELD.OPPONENT_ACCOUNT_AGE_DAYS)) return "P2P_WINS";
  if (hasField(TASK_RULE_FIELD.REFERRED_USER_MADE_QUALIFYING_DEPOSIT))
    return "USER_REFERRED";
  if (hasField(TASK_RULE_FIELD.SHARED_DEVICE_FLAG)) return "CONSECUTIVE_PICK_DAYS";
  if (hasField(TASK_RULE_FIELD.CONSECUTIVE_DAYS)) return "CONSECUTIVE_LOGIN_DAYS";
  return undefined;
};

/** Reconstruct the Bet Currency multi-select from the `money_type` rule (Game
 *  Pass V2), the inverse of `buildMoneyTypeRule`. Handles both `op: "eq"` (a
 *  single currency) and `op: "in"` (both currencies), falling back to the
 *  legacy top-level `required_currencies` column for tasks persisted before the
 *  rules migration (mirroring `resolveSelectionFromRules`). The legacy column's
 *  casing isn't guaranteed to match the lowercase wire values, so values are
 *  normalised case-insensitively before mapping. Returns `[]` when neither
 *  carries a recognised money type so the modal shows an empty selection. */
const resolveBetCurrenciesFromRules = (
  rules: ReadonlyArray<TaskRule> | null | undefined,
  legacy?: ReadonlyArray<string> | null,
): BetCurrency[] => {
  const moneyTypes =
    extractRuleIds(rules, TASK_RULE_FIELD.MONEY_TYPE) ??
    (Array.isArray(legacy) && legacy.length > 0 ? legacy : undefined);
  if (!moneyTypes) return [];
  return moneyTypes
    .map((moneyType) => MONEY_TYPE_TO_BET_CURRENCY[moneyType?.toLowerCase()])
    .filter((currency): currency is BetCurrency => currency !== undefined);
};

/** Tournament ids the BE accepts on `league_id` rules (`sr:tournament:…`, etc.). */
const WIRE_SAFE_LEAGUE_ID_PATTERN = /^sr:(tournament|stage|league):/;

const isWireSafeLeagueId = (value: string): boolean =>
  WIRE_SAFE_LEAGUE_ID_PATTERN.test(value);

/** Maps persisted league rule values onto catalogue tournament ids for the
 *  Sport Specific picker. Display names that cannot be resolved (e.g. while
 *  task-options are still loading, or unknown legacy labels) are dropped so a
 *  re-save never emits a mis-labelled `league_id` value. Values that already
 *  look like BE tournament ids round-trip even when the catalogue is empty. */
const normalizeLeagueSelectionsForOptions = (
  selections: string[] | undefined,
  sportOptions: ReadonlyArray<SportOption>,
): string[] | undefined => {
  if (!selections) return undefined;
  const normalized = selections
    .map((selection) => {
      if (isWireSafeLeagueId(selection)) {
        return selection;
      }
      for (const sport of sportOptions) {
        const match = sport.leagues.find(
          (league) =>
            league.tournamentId === selection ||
            league.value === selection ||
            league.label === selection,
        );
        if (match) {
          const resolved = match.tournamentId ?? match.value;
          return isWireSafeLeagueId(resolved) ? resolved : undefined;
        }
      }
      return undefined;
    })
    .filter((value): value is string => value !== undefined);
  return normalized.length > 0 ? normalized : undefined;
};

/** Maps a single `GET /tasks` record into the editor's `TaskDraft` so the
 *  challenge card's Tasks dropdown can render it. Trigger fields we can
 *  recover from the listing payload are populated; the rest fall back to
 *  the same neutral defaults a fresh draft uses.
 *
 *  Exported for unit testing the rules round-trip (Game Pass V2): the Sport
 *  Specific / casino selections are reconstructed from the `rules` array,
 *  with the legacy top-level columns as a fallback. */
export const mapApiTaskToDraft = (
  task: TaskListRecord,
  sportOptions: ReadonlyArray<SportOption> = [],
  submissionModeDefaults: TaskSubmissionModeDefaults = {},
): TaskDraft => {
  const taskType = mapTaskDataSourceToType(task.data_source);
  // Gate the trigger round-trip by task type, mirroring `buildTaskRules`' write
  // gate exactly: a sportsbook/social task only reconstructs the Sport Specific
  // selection, a casino task only reconstructs the casino provider / game /
  // action / bet-currency selections (Action Amount round-trips from
  // `target_value`). Without this gate a legacy column the BE still echoes for
  // the *other* task type (e.g. a stale `sports` array on a casino task) would
  // silently ride into the draft even though the modal never surfaces it for
  // that task type.
  const isCasino = taskType === "CASINO";
  const isSocial = taskType === "SOCIALS";
  // A sportsbook Streak task drives its goal off `counting_method: "STREAK"`
  // with the streak-day count on `target_value` — so the day count round-trips
  // into the "Streak" card, NOT the "Number of Bets" input.
  const isSportsbookStreak =
    taskType === "SPORTSBOOK" && task.counting_method === "STREAK";
  return {
    id: task.id,
    name: task.name,
    description: task.description ?? "",
    // Carry the persisted image key so the edit modal can rebuild a CloudFront
    // preview URL (see `buildGamePassImageUrl`). `null` → omitted.
    imgKey: task.img_key ?? undefined,
    // Preferred preview source: the BE-provided, ready-to-render URL. `null` →
    // omitted so the preview falls back to the `imgKey` path.
    imageUrl: task.image_url ?? undefined,
    // The task's position within its challenge — stamped by the BE on create
    // (incrementing per task) and overwritten by drag-reorder. The single
    // ordering key (task analogue of a challenge's `sort_order`). See
    // `sortTasksByChallengeOrder`.
    challengeOrder: task.challenge_order ?? null,
    trigger: {
      taskType,
      // The "Streak" condition card round-trips from `target_value` when the
      // task's `counting_method` is `"STREAK"` (sportsbook only). Otherwise
      // undefined so the card stays collapsed.
      streak:
        isSportsbookStreak &&
        task.target_value !== null &&
        task.target_value !== undefined
          ? String(task.target_value)
          : undefined,
      // A plain sportsbook (COUNT) task round-trips its goal count from
      // `target_value` into the "Number of Bets" input (the inverse of
      // `resolveTargetValue`). Gated away from casino / social / streak tasks,
      // whose goal rides a different live input.
      numberOfBets:
        !isCasino &&
        !isSocial &&
        !isSportsbookStreak &&
        task.target_value !== null &&
        task.target_value !== undefined
          ? String(task.target_value)
          : undefined,
      betType: mapTaskBetType(task.bet_type),
      // Bet Currency round-trips from the `money_type` rule (Game Pass V2),
      // handling both a single currency (`eq`) and both (`in`), falling back to
      // the legacy top-level `required_currencies` column for tasks persisted
      // before the rules migration. Gated to sportsbook tasks (the only type
      // that emits a money_type rule) so a stale rule echoed on a casino /
      // social task can't leak a currency the modal never surfaces there.
      betCurrencies:
        isCasino || isSocial
          ? []
          : resolveBetCurrenciesFromRules(
              task.rules,
              task.required_currencies,
            ),
      qualifier: mapTaskQualifier(task.outcome_required),
      oddsRequirement:
        task.min_odds_american !== null && task.min_odds_american !== undefined
          ? String(task.min_odds_american)
          : undefined,
      // Maximum Odds round-trips from the `odds_american` `lte` rule (Game Pass
      // V2 — no longer a top-level column), falling back to the legacy
      // `max_odds_american` column for tasks persisted before the rules
      // migration. The `lte` op disambiguates it from the Minimum Odds `gte`
      // rule that shares the same `odds_american` field.
      maxOddsRequirement: resolveNumericFromRules(
        task.rules,
        TASK_RULE_FIELD.ODDS_AMERICAN,
        task.max_odds_american,
        TASK_RULE_OP.LTE,
      ),
      // The Sport Specific selection round-trips from the `sport_id` condition
      // rule (Game Pass V2), falling back to the legacy top-level `sports`
      // column for tasks persisted before the rules migration. Gated to
      // non-casino tasks so a stale `sports` column echoed on a casino task
      // can't leak a sport selection the casino modal never shows. No selection
      // → undefined so the condition card stays collapsed.
      sportSpecific: isCasino
        ? undefined
        : resolveSelectionFromRules(
            task.rules,
            TASK_RULE_FIELD.SPORT,
            task.sports,
          ),
      // Leagues selected under a sport round-trip from the `league` condition
      // rule. Gated to non-casino tasks (same rationale as `sportSpecific`); no
      // legacy top-level column exists, so only the rule is read.
      sportLeagues: isCasino
        ? undefined
        : normalizeLeagueSelectionsForOptions(
            resolveSelectionFromRules(
              task.rules,
              TASK_RULE_FIELD.LEAGUE_ID,
              null,
            ) ??
              resolveSelectionFromRules(
                task.rules,
                TASK_RULE_FIELD.LEAGUE,
                null,
              ),
            sportOptions,
          ),
      // XP Amount round-trips from the BE `xp_reward`. `0` is kept as "0"
      // (a real, edited value) rather than hidden.
      xpAmount:
        task.xp_reward !== null && task.xp_reward !== undefined
          ? String(task.xp_reward)
          : undefined,
      taskSubmissionMode: resolveTaskSubmissionModeFromApiTask(taskType, task),
      // Number of Legs round-trips from the BE `min_leg_count` (multi-leg
      // tasks). The modal only renders the field when bet_type is Parlay or SGP.
      minLegCount:
        task.min_leg_count !== null && task.min_leg_count !== undefined
          ? String(task.min_leg_count)
          : undefined,
      // Casino provider / game selections round-trip from the `provider` /
      // `game_id` rules (Game Pass V2), falling back to the legacy top-level
      // columns. Gated to casino tasks (symmetric with the sport gate above)
      // so stale casino columns echoed on a sportsbook/social task don't leak
      // in. No selection → undefined so the picker re-opens collapsed.
      casinoProviders: isCasino
        ? resolveSelectionFromRules(
            task.rules,
            TASK_RULE_FIELD.PROVIDER,
            task.casino_providers,
          )
        : undefined,
      casinoGames: isCasino
        ? resolveSelectionFromRules(
            task.rules,
            TASK_RULE_FIELD.GAME,
            task.casino_games,
          )
        : undefined,
      // Chip labels seed from the BE-resolved `games[]` (gamepass-api #371) so
      // reopening a saved task shows names immediately. Ids the BE couldn't
      // resolve (empty `game_name`) are left to the catalogue-lookup fallback
      // (`useResolveCasinoGameLabels`), which only walks unresolved ids.
      casinoGameLabels: isCasino
        ? buildCasinoGameLabelsFromTaskGames(task.games)
        : undefined,
      // Casino Action (Play / Win) round-trips from the `outcome` rule.
      casinoAction: isCasino
        ? resolveCasinoActionFromRules(task.rules)
        : undefined,
      // Action Amount round-trips from the task's top-level `target_value`.
      casinoActionAmount:
        isCasino &&
        task.target_value !== null &&
        task.target_value !== undefined
          ? String(task.target_value)
          : undefined,
      // Casino Bet Currency round-trips from the `money_type` rule (Game Pass
      // V2). No legacy top-level column.
      casinoBetCurrencies: isCasino
        ? resolveCasinoBetCurrenciesFromRules(task.rules)
        : undefined,
      // Casino Wager Amount round-trips from the top-level `min_bet_amount`
      // column (the wagered stake required to qualify — outside `rules`, the
      // mirror of `resolveMinBetAmount`). Gated to casino tasks so a stale value
      // never surfaces on a sportsbook / social task.
      casinoWagerAmount:
        isCasino &&
        task.min_bet_amount !== null &&
        task.min_bet_amount !== undefined
          ? String(task.min_bet_amount)
          : undefined,
      // Minimum Bet Amount round-trips from the top-level `min_bet_amount`
      // column (sportsbook), falling back to the legacy `bet_amount` rule for
      // tasks persisted before the value moved off `rules`. Gated to sportsbook
      // tasks so a stale value never surfaces on casino / social.
      minBetAmount:
        isCasino || isSocial
          ? undefined
          : task.min_bet_amount !== null && task.min_bet_amount !== undefined
            ? String(task.min_bet_amount)
            : resolveNumericFromRules(task.rules, TASK_RULE_FIELD.BET_AMOUNT),
      // Socials trigger round-trips from the social mechanic rule (the inverse
      // of `buildSocialRules`): the Task Type dropdown from the rule `field`,
      // the "X Amount" milestone from `target_value`. Gated to social tasks so
      // a stale rule echoed on another task type can't leak in. No selection →
      // undefined so the modal falls back to its defaults.
      socialTaskType: isSocial
        ? resolveSocialTaskTypeFromRules(task.rules)
        : undefined,
      socialAmount:
        isSocial &&
        task.target_value !== null &&
        task.target_value !== undefined
          ? String(task.target_value)
          : undefined,
      // The live "Number of Bets" input (`numberOfBets` above) is the primary
      // round-trip for a plain sportsbook (COUNT) goal. This preserves the same
      // `target_value` as a fallback so re-saving a task whose Number of Bets
      // input is left blank keeps its stored goal instead of silently resetting
      // it to the default (see `resolveTargetValue`). Gated to plain sportsbook:
      // casino / social / streak tasks source their goal from a different live
      // input, so preserving it here would be redundant (or wrong).
      preservedTargetValue:
        !isCasino &&
        !isSocial &&
        !isSportsbookStreak &&
        typeof task.target_value === "number"
          ? task.target_value
          : undefined,
    },
    rewards: [],
  };
};

/** Selects the tasks that belong to `challenge` from the season-wide task
 *  list. Prefers a strict `challenge_id` match (the reliable key once the
 *  challenge is persisted); falls back to matching on the challenge display
 *  name when the task carries a null `challenge_id` (legacy pre-persist
 *  tasks). `challenge_order` is NOT used for grouping — it's the task's
 *  position *within* the challenge (the sort key), not the challenge's. */
const selectTasksForChallenge = (
  challenge: ChallengeApiRecord,
  seasonTasks: ReadonlyArray<TaskListRecord>,
): ReadonlyArray<TaskListRecord> => {
  const challengeId = challenge.id ?? challenge.challenge_id;
  return seasonTasks.filter((task) => {
    if (task.challenge_id) {
      return task.challenge_id === challengeId;
    }
    return task.challenge === challenge.name;
  });
};

// Exported for unit testing the reward round-trip (BE `rewards` entries →
// editor reward rows): the per-tier duplicates a `BOTH`-path challenge
// persists are collapsed back to one row per authored reward.
export const mapApiChallengeToDraft = (
  challenge: ChallengeApiRecord,
  seasonTasks: ReadonlyArray<TaskListRecord> = [],
  sportOptions: ReadonlyArray<SportOption> = [],
  submissionModeDefaults: TaskSubmissionModeDefaults = {},
): ChallengeDraft => {
  // The BE stores one reward entry per tier the challenge pays out to, so a
  // `BOTH`-path challenge persists each authored reward twice (`free` +
  // `premium`). Re-hydrating every entry would surface those duplicates as
  // separate editor rows, and the next save would re-expand each row across
  // both tiers — doubling the reward count on every edit cycle. Collapse back
  // to the single canonical tier (`mapChallengeRewardsToRequest`'s inverse) so
  // one authored reward stays one editor row regardless of path.
  const canonicalTier: ChallengeRewardTier =
    challenge.path_availability === "PREMIUM" ? "premium" : "free";
  // HOTFIX: the BE has been observed to return `rewards` as a non-array payload
  // (e.g. an empty object) for challenges authored without rewards — violating
  // the typed `CreateChallengeRewardRequest[] | null` contract — which crashed
  // the season detail page with `rewards.filter is not a function`. Guard
  // against anything that isn't a real array before filtering until the BE
  // contract is fixed.
  const rewardIdBase = String(
    challenge.id ?? challenge.challenge_id ?? challenge.name,
  );
  const rewards: RewardDraft[] = Array.isArray(challenge.rewards)
    ? challenge.rewards
        .filter((reward) => reward.tier === canonicalTier)
        .map((reward, index) => mapApiRewardToDraft(reward, rewardIdBase, index))
    : [];

  // Order the challenge's tasks by their persisted `challenge_order` (nulls
  // last) so the editor renders them in creation order, or the order the admin
  // set via drag-reorder once they've reordered them.
  const tasks = sortTasksByChallengeOrder(
    selectTasksForChallenge(challenge, seasonTasks).map((task) =>
      mapApiTaskToDraft(task, sportOptions, submissionModeDefaults),
    ),
  );

  return {
    id: challenge.id ?? challenge.challenge_id ?? challenge.name,
    persistedId: challenge.id ?? challenge.challenge_id ?? undefined,
    pathAvailability:
      challenge.path_availability === "PREMIUM"
        ? "PREMIUM"
        : challenge.path_availability === "BOTH"
          ? "BOTH"
          : "FREE",
    name: challenge.name,
    description: challenge.description ?? "",
    // Carry the persisted icon key so the challenge editor can rebuild a
    // CloudFront preview URL (see `buildGamePassImageUrl`). `null` → omitted.
    imgKey: challenge.img_key ?? undefined,
    // Preferred preview source: the BE-provided, ready-to-render URL. `null` →
    // omitted so the preview falls back to the `imgKey` path.
    imageUrl: challenge.image_url ?? undefined,
    rewards,
    tasks,
    isExpanded: false,
    isSaved: true,
  };
};

/**
 * Best-effort season-level sportsbook submission mode inferred from the
 * season's tasks. The seasons *list* endpoint returns a summary record that
 * omits the behavioural defaults (`sportsbook_submission_mode`), and cloned
 * seasons don't carry it either — so a season saved as "Manual" reads back as
 * the "AUTO" fallback on the Details / View General card.
 *
 * Every sportsbook task is stamped with the season default on save, so we
 * recover it from those tasks when they unanimously agree; mixed modes (from
 * per-task overrides) are ambiguous, so we return `undefined` and let the
 * caller fall back to AUTO.
 *
 * KNOWN LIMITATION: this is a heuristic, not an authoritative read. When the
 * record omits the season default we cannot distinguish "season default =
 * MANUAL, tasks inherit it" from "season default = AUTO, every sportsbook task
 * was individually overridden to MANUAL" — both present as unanimously-Manual
 * tasks, so the latter is reported as MANUAL. The single-task season is the
 * clearest example: one MANUAL-overridden task on an AUTO-default season reads
 * back as MANUAL. This is unavoidable without the BE returning
 * `sportsbook_submission_mode`, and we deliberately optimise for the common
 * case (the season default drives its tasks) over the rare all-overridden one.
 * The record's own field always takes priority when present, so this inference
 * only ever runs for summary list records / clones that drop the field.
 */
const deriveSportsbookSubmissionModeFromTasks = (
  seasonTasks: ReadonlyArray<TaskListRecord>,
): SeasonTaskSubmissionMode | undefined => {
  let resolved: SeasonTaskSubmissionMode | undefined;
  for (const task of seasonTasks) {
    if (mapTaskDataSourceToType(task.data_source) !== "SPORTSBOOK") continue;
    const mode = resolveTaskSubmissionModeFromApiTask("SPORTSBOOK", task);
    if (!mode) continue;
    // Mixed per-task overrides — the season default is ambiguous, so bail out
    // rather than guess.
    if (resolved && resolved !== mode) return undefined;
    resolved = mode;
  }
  return resolved;
};

const mapApiSeasonToSeasonDetailsData = (
  record: SeasonApiRecord,
  seasonChallenges: ReadonlyArray<ChallengeApiRecord>,
  seasonTasks: ReadonlyArray<TaskListRecord> = [],
  sportOptions: ReadonlyArray<SportOption> = [],
): SeasonDetailsData => {
  const bucket = seasonStatusToBucket(record.status);
  // Editability keys off the precise status, NOT the lifecycle bucket:
  // the `scheduled` bucket also contains `locked` seasons (an "Up Next"
  // season frozen for editing), and those must stay read-only on the
  // Details page to match the "Locked" pill the listing card shows.
  // Only true `draft` / `scheduled` seasons get the in-place editor.
  const editable =
    record.status === SEASON_API_STATUS.DRAFT ||
    record.status === SEASON_API_STATUS.SCHEDULED;
  // Dates are persisted in UTC; convert to the admin's local zone so the
  // Schedule pickers show the wall-clock time they originally selected. The
  // edit path (combineDateAndTime) converts back to UTC on save.
  const startDay = dayjs.utc(record.start_date).local();
  const endDay = dayjs.utc(record.end_date).local();

  const mappedSeason = mapApiSeasonToSeasonData(record);
  // The season record is the source of truth when it carries the behavioural
  // defaults; when it doesn't (summary list record / cloned season) we recover
  // the sportsbook default from the season's tasks before falling back to AUTO,
  // so a "Manual" season doesn't silently display as "Auto".
  const sportsbookSubmissionMode =
    record.sportsbook_submission_mode ??
    record.task_submission_mode ??
    deriveSportsbookSubmissionModeFromTasks(seasonTasks) ??
    "AUTO";
  const submissionModeDefaults: TaskSubmissionModeDefaults = {
    sportsbook: sportsbookSubmissionMode,
    casino: record.casino_submission_mode ?? "AUTO",
  };
  return {
    season: mappedSeason,
    mode: editable ? "editable" : "locked",
    general: {
      fullDateRange: formatFullDateTimeRange(record.start_date, record.end_date),
      enrolled: 0,
      avgCompletionPct: 0,
      reportsCount: 0,
      endSeasonEnabled: bucket === SEASON_LIFECYCLE_BUCKET.ACTIVE,
      description: record.description ?? "",
      tiers: {
        free: record.free_path_enabled ?? true,
        premium: record.premium_path_enabled ?? true,
      },
      sportsbookSubmissionMode,
      casinoSubmissionMode: record.casino_submission_mode ?? "AUTO",
      imgKey: record.img_key ?? undefined,
      imageUrl: record.image_url ?? undefined,
      schedule: {
        autoStartAfter: Boolean(record.trigger_season_id),
        previousSeasonId: record.trigger_season_id ?? undefined,
        startDate: startDay.isValid() ? startDay : undefined,
        startTime: startDay.isValid() ? startDay : undefined,
        endDate: endDay.isValid() ? endDay : undefined,
        endTime: endDay.isValid() ? endDay : undefined,
      },
    },
    sequential: mapProgressionModeToSequential(record.progression_mode),
    challenges: [...seasonChallenges]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((challenge) =>
        mapApiChallengeToDraft(
          challenge,
          seasonTasks,
          sportOptions,
          submissionModeDefaults,
        ),
      ),
  };
};

/**
 * Game Pass → Configuration → Seasons → Season Details.
 *
 * Resolves a `:seasonId` route param against `SEASON_DETAILS_BY_ID` and
 * renders the per-season page from the Figma:
 *
 *   - General card (title + status + dates + 3 audience metrics)
 *   - Challenges card (locked summary cards OR full editor)
 *
 * Locked vs editable comes from the mock data's `mode` field, mirroring how
 * the backend will eventually distinguish read-only Active / Up-Next seasons
 * from editable Scheduled / Draft seasons. The page is mock-only today; when
 * the seasons API ships, only `findSeasonDetailsById` changes.
 */
const SeasonDetails: FC = () => {
  const navigate = useNavigate();
  const { seasonId } = useParams<{ seasonId?: string }>();
  const seasonsQuery = useGetSeasonsList();
  const challengesQuery = useGetChallengesList(
    seasonId ? { seasonId } : undefined,
  );
  const tasksQuery = useGetTasksList(seasonId ? { seasonId } : undefined);
  const taskOptionsQuery = useGetTaskOptions();
  const activateSeasonMutation = useActivateSeason();
  const cancelSeasonMutation = useCancelSeason();
  const unlockSeasonMutation = useUnlockSeason();
  const updateSeasonMutation = useUpdateSeason();
  const deleteSeasonMutation = useDeleteSeason();
  const endSeasonMutation = useEndSeason();
  const scheduleSeasonMutation = useScheduleSeason();
  const unscheduleSeasonMutation = useUnscheduleSeason();
  // `state.fromFilter` is set by `SeasonsList.handleOpenSeason` when
  // the user navigates here from a card; it tells us which filter tab
  // the user was viewing so every back-navigation off this page
  // (Back button, End Season confirm, Save, Save as Draft, Delete) can
  // restore that tab on the listing. `parseFilterId` rejects unknown
  // values so a hand-crafted `state` (or a forward-incompatible older
  // listing) cannot push an invalid filter into the URL. When the
  // state is absent (deep-linked Details URL, refreshed page) we fall
  // back to the bare listing URL — same behaviour as before this
  // round-trip was added.
  const location = useLocation();
  const fromFilter = parseFilterId(
    (location.state as { fromFilter?: string } | null | undefined)
      ?.fromFilter,
  );
  const { setActiveSidebarTab, adminReadOnly } = useContext(
    AppContext,
  ) as AppContextValue;
  // UX half of the read-only gamepass guard in PrivateAdminPanelInstance.
  const isReadOnly = adminReadOnly === true;

  const apiSeason = useMemo<SeasonApiRecord | undefined>(
    () =>
      seasonsQuery.data?.seasons.find(
        (record) => (record.id ?? record.season_id) === seasonId,
      ),
    [seasonId, seasonsQuery.data?.seasons],
  );

  // The active season this one auto-starts after — drives the Schedule /
  // Edit-Season auto-fill (active end + 1 min) and the `trigger_season_id` link
  // (FE-1970). This season is excluded so it can never anchor to itself.
  const autoStartAnchor = useMemo(
    () =>
      resolveAutoStartAnchor(seasonsQuery.data?.seasons ?? [], seasonId),
    [seasonsQuery.data?.seasons, seasonId],
  );

  const apiChallenges = useMemo<ReadonlyArray<ChallengeApiRecord>>(
    () => challengesQuery.data?.challenges ?? [],
    [challengesQuery.data?.challenges],
  );

  const apiTasks = useMemo<ReadonlyArray<TaskListRecord>>(
    () => tasksQuery.data?.tasks ?? [],
    [tasksQuery.data?.tasks],
  );

  const sportOptions = useMemo(
    () => getSportOptions(taskOptionsQuery.data),
    [taskOptionsQuery.data],
  );

  const details = useMemo<SeasonDetailsData | undefined>(() => {
    if (apiSeason) {
      return mapApiSeasonToSeasonDetailsData(
        apiSeason,
        apiChallenges,
        apiTasks,
        sportOptions,
      );
    }
    return findSeasonDetailsById(seasonId);
  }, [apiChallenges, apiTasks, apiSeason, seasonId, sportOptions]);

  // ─── Editable mode local state ─────────────────────────────────────────
  //
  // Scheduled / Draft seasons drop the live `ChallengesAndTasks` editor in
  // place, which expects a `ChallengesValues` it owns the canonical copy of.
  // We seed that state from the mock when the route param resolves so the
  // editor is fully functional in-place (add / duplicate / delete challenge,
  // open the task modal, …) without any persistence wiring.
  const [editableValues, setEditableValues] = useState<ChallengesValues>(() =>
    details
      ? {
          sequential: details.sequential,
          challenges: details.challenges,
          ...deriveSameXpReward(details.challenges),
        }
      : { sequential: true, challenges: [] },
  );
  const [isEndSeasonOpen, setIsEndSeasonOpen] = useState(false);
  // FE-2261: gates the production type-to-confirm End Season modal
  // (FE-2247). Off / still-loading falls back to the legacy single-click
  // confirmation so the action is never unreachable.
  const { isEnabled: isEndSeasonConfirmV2Enabled } =
    usePamFeature("endSeasonConfirmV2");
  // Footer destructive action (Delete / Unschedule) confirmation. The
  // editable footer's left button used to be a redundant "Discard"
  // (it only mirrored the back arrow / breadcrumb). It now drives the
  // same lifecycle-destructive action the listing card and Edit modal
  // expose, guarded by this confirmation so a misclick can't delete a
  // season outright.
  const [isDestructiveConfirmOpen, setIsDestructiveConfirmOpen] =
    useState(false);
  const [activationModalState, setActivationModalState] = useState<{
    open: boolean;
    force: boolean;
    blockerMessage?: string;
  }>({
    open: false,
    force: false,
  });
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [isEditGeneralOpen, setIsEditGeneralOpen] = useState(false);
  // Read-only counterpart to `isEditGeneralOpen`, driven by the eye icon next
  // to the "Locked" badge on a locked / active season. Kept separate so the
  // view path (eye → read-only modal) never collides with the editable path
  // (pencil → editable modal) — the two modes are mutually exclusive per
  // `isLocked`, but the distinct state keeps each surface's intent explicit.
  const [isViewGeneralOpen, setIsViewGeneralOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);

  // Local mirror of the editable General fields (name, description,
  // tiers, schedule). The page seeds it from the resolved mock and
  // overwrites it on Save so the General card visibly reflects the
  // admin's changes until the API is wired. The `season.name` shown in
  // the breadcrumb / general tile reads from this mirror in editable
  // mode for the same reason.
  const [editableGeneral, setEditableGeneral] = useState<EditSeasonValues>(
    () => seedEditableGeneral(details),
  );
  const pageTitle =
    details?.mode === "locked"
      ? details.season.name
      : editableGeneral.name ||
        details?.season.name ||
        AppStrings.GAME_PASS_SEASON_CONFIGURATION;
  usePageTitle(pageTitle);

  const [scheduleDraft, setScheduleDraft] = useState<ScheduleValues>(
    () => seedEditableGeneral(details).schedule,
  );

  // Seed the editable state ONCE per resolved season, not on every `details`
  // recompute. `details` changes on every background refetch (e.g. after a
  // create/edit/delete task invalidates the tasks query); re-seeding on those
  // would clobber the admin's in-session edits — most visibly flipping the
  // "Same XP reward for all tasks" toggle back on (and locking the XP field)
  // the moment a task is added. Tracking the seeded season id lets us still
  // re-seed when the user navigates to a different season in the same session.
  // `hasSeededRef` is the explicit "have we seeded yet?" flag (clearer than
  // overloading a nullable id with an implicit `undefined = never seeded`
  // sentinel); `seededSeasonIdRef` records which season that seed was for so a
  // genuine navigation to a different season still re-seeds.
  const hasSeededRef = useRef(false);
  const seededSeasonIdRef = useRef<string | null>(null);
  // The challenges/tasks queries are independent of the seasons query, so when
  // the user opens this page from the (already cached) listing, `apiSeason`
  // resolves instantly while challenges/tasks are still in flight — making
  // `details` first resolve with empty challenges. Seeding the one-shot editor
  // state from that empty snapshot (and flipping the guard) is what left the
  // editor blank until a manual refresh. Wait until both per-season queries
  // have settled with their own data — not loading and not showing the prior
  // season's placeholder — so the single seed always captures the real data.
  const challengesReady =
    !seasonId ||
    (!challengesQuery.isLoading && !challengesQuery.isPlaceholderData);
  const tasksReady =
    !seasonId || (!tasksQuery.isLoading && !tasksQuery.isPlaceholderData);
  useEffect(() => {
    if (!details) return;
    if (!challengesReady || !tasksReady) return;
    const resolvedSeasonId = seasonId ?? null;
    if (hasSeededRef.current && seededSeasonIdRef.current === resolvedSeasonId) {
      return;
    }
    hasSeededRef.current = true;
    seededSeasonIdRef.current = resolvedSeasonId;
    setEditableValues({
      sequential: details.sequential,
      challenges: details.challenges,
      ...deriveSameXpReward(details.challenges),
    });
    setEditableGeneral(seedEditableGeneral(details));
    setScheduleDraft(seedEditableGeneral(details).schedule);
  }, [details, seasonId, challengesReady, tasksReady]);

  useEffect(() => {
    setActiveSidebarTab(
      SIDER_TABS?.GAME_PASS,
      GAME_PASS_DROPDOWN_LABELS.SEASONS,
    );
  }, [setActiveSidebarTab]);

  // Single source of truth for every "go back to the listing"
  // navigation off this page. Builds the listing URL with the
  // originating filter encoded as `?filter=<id>` so the listing
  // remounts on the same tab the user came from. Falls back to the
  // bare listing URL when no `fromFilter` is in scope (deep-linked
  // Details, refreshed page) — matches the pre-round-trip behaviour
  // so existing deep-link contracts aren't broken.
  const backToSeasons = useCallback(() => {
    const base = NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_SEASONS;
    navigate(
      fromFilter ? `${base}?${SEASONS_FILTER_QUERY_PARAM}=${fromFilter}` : base,
    );
  }, [fromFilter, navigate]);

  const handleBack = useCallback(() => {
    backToSeasons();
  }, [backToSeasons]);

  const handleOpenEndSeason = useCallback(() => setIsEndSeasonOpen(true), []);
  const handleCancelEndSeason = useCallback(
    () => setIsEndSeasonOpen(false),
    [],
  );
  const handleConfirmEndSeason = useCallback(
    // Audit fields come from the v2 type-to-confirm modal; the legacy
    // single-click modal (FE-2261 flag-off) confirms without them.
    async (confirmation?: EndSeasonAudit) => {
      // No resolved season id (deep-linked mock route) — preserve the
      // pre-API behaviour: close the modal and bounce back to the listing.
      if (!seasonId) {
        setIsEndSeasonOpen(false);
        backToSeasons();
        return;
      }
      try {
        await endSeasonMutation.mutateAsync({
          seasonId,
          ...(confirmation ? { audit: confirmation } : {}),
        });
        setIsEndSeasonOpen(false);
        backToSeasons();
      } catch (error) {
        message.error(getApiErrorMessage(error, "Failed to end season"));
      }
    },
    [backToSeasons, endSeasonMutation, seasonId],
  );

  const handleOpenActivateSeason = useCallback(() => {
    setActivationModalState({ open: true, force: false });
  }, []);

  const handleCloseActivateSeason = useCallback(() => {
    setActivationModalState({ open: false, force: false });
  }, []);

  const handleConfirmActivateSeason = useCallback(async () => {
    if (!seasonId) return;
    const variables: ActivateSeasonVariables = {
      seasonId,
      ...(activationModalState.force ? { force: true } : {}),
    };
    try {
      await activateSeasonMutation.mutateAsync(variables);
      setActivationModalState({ open: false, force: false });
    } catch (error) {
      if (!activationModalState.force && shouldOfferForceActivate(error)) {
        setActivationModalState({
          open: true,
          force: true,
          blockerMessage: getApiErrorMessage(error, ""),
        });
        return;
      }
      message.error(getApiErrorMessage(error, "Failed to activate season"));
    }
  }, [activateSeasonMutation, activationModalState.force, seasonId]);

  const navigateToCanceled = useCallback(() => {
    const base = NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_SEASONS;
    navigate(`${base}?${SEASONS_FILTER_QUERY_PARAM}=canceled`);
  }, [navigate]);

  const handleOpenCancelSeason = useCallback(() => {
    setIsCancelConfirmOpen(true);
  }, []);

  const handleCloseCancelSeason = useCallback(() => {
    setIsCancelConfirmOpen(false);
  }, []);

  const handleConfirmCancelSeason = useCallback(async () => {
    if (!seasonId) return;
    try {
      await cancelSeasonMutation.mutateAsync(seasonId);
      setIsCancelConfirmOpen(false);
      navigateToCanceled();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to cancel season"));
    }
  }, [cancelSeasonMutation, navigateToCanceled, seasonId]);

  // Unlock fires immediately on click (no confirmation modal). On success
  // the season transitions `locked` → `scheduled`; the mutation refreshes
  // the seasons-list cache, so `details` re-resolves to editable mode and
  // the page swaps the locked view for the in-place editor automatically.
  const handleUnlockSeason = useCallback(async () => {
    if (!seasonId) return;
    try {
      await unlockSeasonMutation.mutateAsync(seasonId);
      message.success(AppStrings.SEASON_DETAILS_UNLOCK_SUCCESS);
    } catch (error) {
      message.error(
        getApiErrorMessage(error, AppStrings.SEASON_DETAILS_UNLOCK_ERROR),
      );
    }
  }, [seasonId, unlockSeasonMutation]);

  const handleSeeAllReports = useCallback(() => {
    // Reports route doesn't ship until the backend payload lands. Stubbed
    // so the click target is visibly wired in the meantime — same pattern
    // as `handleSaveAsDraft` in the wizard.
  }, []);

  const handleSaveEditableChanges = useCallback(() => {
    // Mock — persistence will land with the seasons API.
    backToSeasons();
  }, [backToSeasons]);

  // Pencil-edit button on the editable General card. Opens the Edit
  // Season modal (Figma — General + Scheduling form). All persistence
  // is mock today: Save flushes the modal's draft back into
  // `editableGeneral` so the General card visibly reflects the change;
  // Save as Draft / Delete navigate back to the listing per the
  // existing `handleSaveAsDraft` / End Season patterns.
  const handleEditGeneral = useCallback(() => {
    setIsEditGeneralOpen(true);
  }, []);
  const handleCloseEditGeneral = useCallback(
    () => setIsEditGeneralOpen(false),
    [],
  );
  const handleViewGeneral = useCallback(() => {
    setIsViewGeneralOpen(true);
  }, []);
  const handleCloseViewGeneral = useCallback(
    () => setIsViewGeneralOpen(false),
    [],
  );
  const handleSaveEditGeneral = useCallback(
    async (next: EditSeasonValues) => {
      if (!seasonId || !apiSeason) {
        setEditableGeneral(next);
        setIsEditGeneralOpen(false);
        return;
      }

      // Run the overlap guard for any non-DRAFT season (SCHEDULED, ACTIVE,
      // etc.). A DRAFT's dates aren't locked in yet, so editing its general
      // details must never surface a conflict — that check is deferred to the
      // moment it's actively scheduled (`handleScheduleSeason`, FE-1693).
      if (apiSeason.status !== SEASON_API_STATUS.DRAFT) {
        const conflict = findSeasonScheduleConflict({
          schedule: next.schedule,
          existingSeasons: seasonsQuery.data?.seasons ?? [],
          excludeSeasonId: seasonId,
        });
        if (conflict) {
          message.error(buildSeasonScheduleConflictMessage(conflict));
          return;
        }
      }

      let stamped: EditSeasonValues;
      try {
        stamped = await stampSeasonBannerForSave(next);
      } catch (error) {
        message.error(
          getApiErrorMessage(
            error,
            AppStrings.GAME_PASS_SEASON_BANNER_UPLOAD_ERROR,
          ),
        );
        return;
      }

      const body = mapEditSeasonValuesToUpdateSeasonRequest({
        current: apiSeason,
        next: stamped,
      });

      if (Object.keys(body).length === 0) {
        setEditableGeneral(stamped);
        setIsEditGeneralOpen(false);
        return;
      }

      try {
        await updateSeasonMutation.mutateAsync({
          seasonId,
          body,
        });
        setEditableGeneral(stamped);
        setIsEditGeneralOpen(false);
      } catch (error) {
        message.error(
          getApiErrorMessage(error, "Failed to update season"),
        );
      }
    },
    [apiSeason, seasonId, seasonsQuery.data?.seasons, updateSeasonMutation],
  );
  const handleSaveEditGeneralAsDraft = useCallback(
    async (next: EditSeasonValues) => {
      if (!seasonId || !apiSeason) {
        setEditableGeneral(next);
        setIsEditGeneralOpen(false);
        backToSeasons();
        return;
      }

      // Same overlap guard as Save — any status !== DRAFT. Draft edits defer
      // the check to schedule.
      if (apiSeason.status !== SEASON_API_STATUS.DRAFT) {
        const conflict = findSeasonScheduleConflict({
          schedule: next.schedule,
          existingSeasons: seasonsQuery.data?.seasons ?? [],
          excludeSeasonId: seasonId,
        });
        if (conflict) {
          message.error(buildSeasonScheduleConflictMessage(conflict));
          return;
        }
      }

      let stamped: EditSeasonValues;
      try {
        stamped = await stampSeasonBannerForSave(next);
      } catch (error) {
        message.error(
          getApiErrorMessage(
            error,
            AppStrings.GAME_PASS_SEASON_BANNER_UPLOAD_ERROR,
          ),
        );
        return;
      }

      const body = mapEditSeasonValuesToUpdateSeasonRequest({
        current: apiSeason,
        next: stamped,
      });

      if (Object.keys(body).length > 0) {
        try {
          await updateSeasonMutation.mutateAsync({
            seasonId,
            body,
          });
        } catch (error) {
          message.error(
            getApiErrorMessage(error, "Failed to update season"),
          );
          return;
        }
      }

      setEditableGeneral(stamped);
      setIsEditGeneralOpen(false);
      backToSeasons();
    },
    [apiSeason, backToSeasons, seasonId, seasonsQuery.data?.seasons, updateSeasonMutation],
  );
  const navigateToDrafts = useCallback(() => {
    const base = NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_SEASONS;
    navigate(`${base}?${SEASONS_FILTER_QUERY_PARAM}=drafts`);
  }, [navigate]);

  const handleDeleteSeason = useCallback(async () => {
    if (!seasonId) {
      setIsEditGeneralOpen(false);
      backToSeasons();
      return;
    }
    try {
      await deleteSeasonMutation.mutateAsync(seasonId);
      setIsEditGeneralOpen(false);
      backToSeasons();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to delete season"));
    }
  }, [backToSeasons, deleteSeasonMutation, seasonId]);

  const handleUnscheduleSeason = useCallback(async () => {
    if (!seasonId) {
      setIsEditGeneralOpen(false);
      navigateToDrafts();
      return;
    }
    try {
      await unscheduleSeasonMutation.mutateAsync(seasonId);
      setIsEditGeneralOpen(false);
      navigateToDrafts();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to unschedule season"));
    }
  }, [navigateToDrafts, seasonId, unscheduleSeasonMutation]);

  const handleOpenSchedule = useCallback(() => {
    // A draft can only be scheduled once it's structurally complete: at least
    // one challenge, and every challenge holds at least one task. Surface the
    // first offending challenge by name so the admin knows where to add a task,
    // and keep the modal closed until the season is ready.
    const readinessIssue = findSeasonScheduleReadinessIssue(
      editableValues.challenges,
    );
    if (readinessIssue) {
      message.error(buildSeasonScheduleReadinessMessage(readinessIssue));
      return;
    }

    setScheduleDraft(editableGeneral.schedule);
    setIsScheduleOpen(true);
  }, [editableGeneral.schedule, editableValues.challenges]);

  const handleCloseSchedule = useCallback(() => setIsScheduleOpen(false), []);

  const handleScheduleSeason = useCallback(async () => {
    if (!seasonId) return;

    const nextGeneral = { ...editableGeneral, schedule: scheduleDraft };

    // Block a schedule whose dates overlap another active / scheduled season
    // (FE-1693) before any write. Excludes this season so its own current
    // window doesn't count as a conflict.
    const conflict = findSeasonScheduleConflict({
      schedule: scheduleDraft,
      existingSeasons: seasonsQuery.data?.seasons ?? [],
      excludeSeasonId: seasonId,
    });
    if (conflict) {
      message.error(buildSeasonScheduleConflictMessage(conflict));
      return;
    }

    try {
      if (apiSeason) {
        const body = mapEditSeasonValuesToUpdateSeasonRequest({
          current: apiSeason,
          next: nextGeneral,
        });
        if (Object.keys(body).length > 0) {
          await updateSeasonMutation.mutateAsync({
            seasonId,
            body,
          });
        }
      }
      await scheduleSeasonMutation.mutateAsync(seasonId);
      setEditableGeneral(nextGeneral);
      setIsScheduleOpen(false);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to schedule season"));
    }
  }, [
    apiSeason,
    editableGeneral,
    scheduleDraft,
    scheduleSeasonMutation,
    seasonId,
    seasonsQuery.data?.seasons,
    updateSeasonMutation,
  ]);

  if (!details && (seasonsQuery.isLoading || challengesQuery.isLoading)) {
    return (
      <>
        <div className={styles.page} data-testid="season-details-loading">
          <div className={styles.header}>
            <button
              type="button"
              className={styles.backButton}
              aria-label={AppStrings.SEASON_DETAILS_BACK_ARIA}
              onClick={handleBack}
              data-testid="season-details-back"
            >
              <LeftOutlined className={styles.backButtonIcon} />
            </button>
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              <span>{AppStrings.SEASON_DETAILS_BREADCRUMB_SEASONS}</span>
            </nav>
          </div>
          <div className={styles.notFound}>
            <p className={styles.notFoundDescription}>
              {AppStrings.SEASONS_LIST_LOADING}
            </p>
          </div>
        </div>
      </>
    );
  }

  // ─── Not-found branch ──────────────────────────────────────────────────
  if (!details) {
    return (
      <>
        <div className={styles.page} data-testid="season-details-not-found">
          <div className={styles.header}>
            <button
              type="button"
              className={styles.backButton}
              aria-label={AppStrings.SEASON_DETAILS_BACK_ARIA}
              onClick={handleBack}
              data-testid="season-details-back"
            >
              <LeftOutlined className={styles.backButtonIcon} />
            </button>
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              <span>{AppStrings.SEASON_DETAILS_BREADCRUMB_SEASONS}</span>
            </nav>
          </div>

          <div className={styles.notFound}>
            <h2 className={styles.notFoundTitle}>
              {AppStrings.SEASON_DETAILS_NOT_FOUND_TITLE}
            </h2>
            <p className={styles.notFoundDescription}>
              {AppStrings.SEASON_DETAILS_NOT_FOUND_DESCRIPTION}
            </p>
            <Button
              intent="primary"
              onClick={handleBack}
              data-testid="season-details-not-found-back"
            >
              {AppStrings.SEASON_DETAILS_BACK_TO_LIST}
            </Button>
          </div>
        </div>
      </>
    );
  }

  const { season, mode, general, sequential, challenges } = details;
  const isLocked = mode === "locked";
  // Editable mode renders the locally-maintained `editableGeneral.name`
  // so Save inside the Edit modal flows back to the page chrome (the
  // breadcrumb's current crumb + the general tile). Locked mode keeps
  // the canonical `season.name` since nothing in this view can mutate
  // it. The Edit modal's title also reads from `displayName` so the
  // user sees the in-progress name update reflected after Save.
  const displayName = isLocked ? season.name : editableGeneral.name;

  // Status pill resolution. Precedence:
  //   1. If the season carries an explicit `status` payload (today: Active,
  //      Up Next, Draft) use its label + the matching tone class so the
  //      General card reads identically to the listing card.
  //   2. Otherwise — and only in editable mode — fall back to the implicit
  //      "Scheduled (Locks in <countdown>)" pill the page composes from the
  //      listing data's `countdown` field. This is the path Scheduled mocks
  //      (September / Fall 2026) ship with today; they have no `status`
  //      because the listing card's countdown alone is sufficient there.
  // Drafts intentionally carry no `countdown`, so the inline `(Locks in …)`
  // tail naturally drops away for them — matching the Figma.
  const explicitStatus = season.status;
  const statusToneClassName = (() => {
    if (explicitStatus) {
      if (explicitStatus.tone === "active") return styles.generalStatusActive;
      if (explicitStatus.tone === "upNext") return styles.generalStatusUpNext;
      if (explicitStatus.tone === "draft") return styles.generalStatusDraft;
      // Completed seasons render the same muted-grey tone as the listing
      // card's `.statusCompleted` (both pull from
      // `--COLOR-TEXT_INPUT_PLACEHOLDER_TEXT`), so the badge reads as
      // "past / inactive" identically across the two surfaces.
      if (explicitStatus.tone === "completed") {
        return styles.generalStatusCompleted;
      }
      return "";
    }
    return isLocked ? "" : styles.generalStatusScheduled;
  })();
  const statusLabel =
    explicitStatus?.label ??
    (isLocked ? undefined : AppStrings.SEASON_STATUS_SCHEDULED);
  const showStatus = Boolean(statusLabel);
  // Drafts surface a primary "Schedule" CTA next to the pencil so admins
  // can promote a draft straight from the General card. The check is
  // tone-based (not `mode`-based) so a future "Up Next" or other
  // editable lifecycle that ships its own status doesn't accidentally
  // inherit the affordance.
  const isDraft = explicitStatus?.tone === "draft";
  const canActivate =
    !isReadOnly && isLocked && explicitStatus?.tone === "upNext";
  // Only `locked` ("Up Next") seasons can be unlocked back to `scheduled`.
  // An Active season has no unlock affordance — it shares the `isLocked`
  // read-only view but isn't a frozen-future season, so gate on the same
  // upNext tone the Activate / Cancel CTAs use.
  const canUnlock = canActivate;
  const canUnschedule = !isLocked && !isDraft && !explicitStatus;
  const destructiveActionLabel = isDraft
    ? AppStrings.EDIT_SEASON_DELETE_SEASON
    : canUnschedule
      ? AppStrings.EDIT_SEASON_UNSCHEDULE_SEASON
      : undefined;

  return (
    <>
      <div className={styles.page} data-testid="season-details">
        {/* ── Breadcrumb + back ───────────────────────────────────────── */}
        <div className={styles.header}>
          <button
            type="button"
            className={styles.backButton}
            aria-label={AppStrings.SEASON_DETAILS_BACK_ARIA}
            onClick={handleBack}
            data-testid="season-details-back"
          >
            <LeftOutlined className={styles.backButtonIcon} />
          </button>
          <nav className={styles.breadcrumb} aria-label="Breadcrumb">
            <button
              type="button"
              className={styles.breadcrumbLink}
              onClick={handleBack}
              data-testid="season-details-breadcrumb-seasons"
            >
              {AppStrings.SEASON_DETAILS_BREADCRUMB_SEASONS}
            </button>
            <span className={styles.breadcrumbSeparator} aria-hidden="true">
              /
            </span>
            <span
              className={styles.breadcrumbCurrent}
              data-testid="season-details-breadcrumb-current"
            >
              {displayName}
            </span>
          </nav>
        </div>

        {/* ── General section ─────────────────────────────────────────── */}
        <section
          className={styles.section}
          aria-labelledby="season-details-general-heading"
          data-testid="season-details-general"
        >
          <h2
            id="season-details-general-heading"
            className={styles.sectionHeading}
          >
            {AppStrings.SEASON_DETAILS_GENERAL}
          </h2>

          {/* Locked seasons render the 4-column grid with audience metric
              tiles. Editable seasons collapse to just the wide summary
              tile (no enrolled / completion / reports data exists for an
              unstarted season) and gain a top-right pencil edit icon. */}
          <div
            className={`${styles.generalGrid} ${
              isLocked ? "" : styles.generalGridEditable
            }`}
          >
            <div
              className={`${styles.generalTile} ${styles.generalTileWide}`}
              data-testid="season-details-general-summary"
            >
              <div className={styles.generalTitleRow}>
                <span className={styles.generalName}>{displayName}</span>
                {isLocked && (
                  <span
                    className={styles.generalLockedBadge}
                    data-testid="season-details-locked-badge"
                  >
                    {AppStrings.SEASON_CARD_LOCKED}
                    <InfoCircleOutlined
                      className={styles.generalLockedBadgeIcon}
                      aria-hidden="true"
                    />
                  </span>
                )}
                {isLocked && (
                  <button
                    type="button"
                    className={styles.viewSeasonButton}
                    aria-label={AppStrings.SEASON_OPEN_ARIA}
                    onClick={handleViewGeneral}
                    data-testid="season-details-view-general"
                  >
                    <EyeOutlined aria-hidden="true" />
                  </button>
                )}
                {canUnlock && (
                  <button
                    type="button"
                    className={styles.unlockSeasonButton}
                    aria-label={AppStrings.SEASON_DETAILS_UNLOCK_ARIA}
                    onClick={handleUnlockSeason}
                    disabled={unlockSeasonMutation.isPending}
                    data-testid="season-details-unlock"
                  >
                    {AppStrings.SEASON_DETAILS_UNLOCK}
                  </button>
                )}
              </div>
              {showStatus && (
                <span
                  className={`${styles.generalStatus} ${statusToneClassName}`}
                  data-testid="season-details-status"
                >
                  {isLocked && explicitStatus?.tone === "active" && (
                    <span
                      className={styles.generalStatusDot}
                      aria-hidden="true"
                    />
                  )}
                  <span className={styles.generalStatusLabel}>
                    {statusLabel}
                  </span>
                  {/* The inline countdown + info-icon tail belongs to the
                      implicit "Scheduled (Locks in …)" fallback only —
                      Drafts (and any future explicit-status editable
                      lifecycle) keep the bare label so the badge tone
                      stays the story, not the countdown. */}
                  {!isLocked && !explicitStatus && season.countdown && (
                    <>
                      <span className={styles.generalStatusCountdown}>
                        {"("}
                        {`${season.countdown.prefix} `}
                        <strong className={styles.generalStatusCountdownValue}>
                          {season.countdown.value}
                        </strong>
                        {")"}
                      </span>
                      <InfoCircleOutlined
                        className={styles.generalStatusInfoIcon}
                        aria-label={
                          AppStrings.SEASON_DETAILS_LOCKS_INFO_ARIA
                        }
                        role="img"
                        data-testid="season-details-status-info"
                      />
                    </>
                  )}
                </span>
              )}
              <span
                className={styles.generalDates}
                data-testid="season-details-dates"
              >
                {general.fullDateRange}
              </span>
              {!isReadOnly && general.endSeasonEnabled && (
                <button
                  type="button"
                  className={styles.endSeasonButton}
                  onClick={handleOpenEndSeason}
                  data-testid="season-details-end-season"
                >
                  {AppStrings.SEASON_DETAILS_END_SEASON}
                </button>
              )}

              {canActivate && (
                <button
                  type="button"
                  className={styles.scheduleButton}
                  aria-label={AppStrings.SEASON_DETAILS_ACTIVATE_ARIA}
                  onClick={handleOpenActivateSeason}
                  data-testid="season-details-activate"
                >
                  {AppStrings.SEASON_DETAILS_ACTIVATE}
                </button>
              )}

              {canActivate && (
                <button
                  type="button"
                  className={styles.cancelSeasonButton}
                  aria-label={`${AppStrings.CANCEL_SEASON} ${displayName}`}
                  onClick={handleOpenCancelSeason}
                  data-testid="season-details-cancel"
                >
                  {AppStrings.CANCEL_SEASON}
                </button>
              )}

              {!isReadOnly && !isLocked && (
                <div
                  className={styles.generalActions}
                  data-testid="season-details-general-actions"
                >
                  {isDraft && (
                    <button
                      type="button"
                      className={styles.scheduleButton}
                      aria-label={
                        AppStrings.SEASON_DETAILS_SCHEDULE_ARIA
                      }
                      onClick={handleOpenSchedule}
                      data-testid="season-details-schedule"
                    >
                      {AppStrings.SEASON_DETAILS_SCHEDULE}
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.editButton}
                    aria-label={AppStrings.SEASON_EDIT_ARIA}
                    onClick={handleEditGeneral}
                    data-testid="season-details-edit-general"
                  >
                    <EditOutlined aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>

            {/* Audience metrics only make sense for a season the player
                base has had a chance to engage with — keep them locked
                to the Active / Up-Next view. */}
            {isLocked && (
              <>
                <MetricTile
                  label={AppStrings.SEASON_DETAILS_METRIC_ENROLLED}
                  value={general.enrolled.toLocaleString()}
                  testId="season-details-metric-enrolled"
                />
                <MetricTile
                  label={AppStrings.SEASON_DETAILS_METRIC_AVG_COMPLETION}
                  value={`${general.avgCompletionPct}%`}
                  testId="season-details-metric-completion"
                />
                <MetricTile
                  label={AppStrings.SEASON_DETAILS_METRIC_ALL_REPORTS}
                  onSeeAll={handleSeeAllReports}
                  testId="season-details-metric-reports"
                />
              </>
            )}
          </div>
        </section>

        {/* ── Challenges section ──────────────────────────────────────── */}
        <section
          className={styles.section}
          aria-labelledby="season-details-challenges-heading"
          data-testid="season-details-challenges"
        >
          <h2
            id="season-details-challenges-heading"
            className={styles.sectionHeading}
          >
            {AppStrings.SEASON_DETAILS_CHALLENGES_HEADING}
          </h2>

          <div className={styles.challengesCard}>
            {challengesQuery.isLoading || tasksQuery.isLoading ? (
              <div
                className={styles.challengesLoading}
                data-testid="season-details-challenges-loading"
              >
                <Spin />
              </div>
            ) : isLocked ? (
              <LockedChallengesView
                sequential={sequential}
                challenges={challenges}
              />
            ) : (
              <ChallengesAndTasks
                values={editableValues}
                onChange={setEditableValues}
                variant="embedded"
                seasonId={seasonId}
                enabledTiers={general.tiers}
                sportsbookSubmissionMode={general.sportsbookSubmissionMode}
                casinoSubmissionMode={general.casinoSubmissionMode}
              />
            )}
          </div>
        </section>

        {/* ── Editable mode footer ────────────────────────────────────── */}
        {!isLocked && (
          <div className={styles.footer}>
            {/* Editable mode is only ever reached for Draft or Scheduled
                seasons (see `mapApiSeasonToSeasonDetailsData`), and both
                resolve a `destructiveActionLabel`: Draft → "Delete Season",
                Scheduled → "Unschedule Season". No editable lifecycle leaves
                the label undefined, so the footer never falls back to a plain
                Discard button — the guard below is a type-narrowing safeguard,
                not a reachable alternate branch. */}
            {destructiveActionLabel && (
              <Button
                intent="danger"
                variant="outline"
                className={styles.destructiveAction}
                onClick={() => setIsDestructiveConfirmOpen(true)}
                data-testid="season-details-delete-season"
              >
                {destructiveActionLabel}
              </Button>
            )}
            <Button
              intent="primary"
              onClick={handleSaveEditableChanges}
              data-testid="season-details-save"
            >
              {AppStrings.SEASON_DETAILS_SAVE_CHANGES}
            </Button>
          </div>
        )}

        {/* ── Footer destructive (Delete / Unschedule) confirmation ───── */}
        {!isLocked && destructiveActionLabel && (
          <Modal
            open={isDestructiveConfirmOpen}
            onClose={() => setIsDestructiveConfirmOpen(false)}
            intent="destructive"
            size="sm"
            destroyOnHidden
            title={
              <span className={styles.endSeasonModalTitle}>
                {isDraft
                  ? AppStrings.DELETE_SEASON_MODAL_TITLE
                  : AppStrings.UNSCHEDULE_SEASON_MODAL_TITLE}
              </span>
            }
            footer={
              <div className={styles.endSeasonModalFooter}>
                <Button
                  intent="neutral"
                  variant="outline"
                  onClick={() => setIsDestructiveConfirmOpen(false)}
                  data-testid="season-details-delete-go-back"
                >
                  {AppStrings.DELETE_SEASON_GO_BACK}
                </Button>
                <Button
                  intent="danger"
                  variant="outline"
                  className={styles.destructiveAction}
                  onClick={() => {
                    setIsDestructiveConfirmOpen(false);
                    if (isDraft) {
                      void handleDeleteSeason();
                    } else {
                      void handleUnscheduleSeason();
                    }
                  }}
                  data-testid="season-details-delete-confirm"
                >
                  {isDraft
                    ? AppStrings.DELETE_SEASON_CONFIRM_CTA
                    : AppStrings.UNSCHEDULE_SEASON_CONFIRM_CTA}
                </Button>
              </div>
            }
          >
            <div data-testid="season-details-delete-modal">
              <p className={styles.endSeasonModalBody}>
                {isDraft
                  ? `${AppStrings.DELETE_SEASON_CONFIRM_BODY_PREFIX} `
                  : `${AppStrings.UNSCHEDULE_SEASON_CONFIRM_BODY_PREFIX} `}
                {!isDraft && (
                  <span className={styles.endSeasonModalName}>{`"${displayName}"`}</span>
                )}
                {isDraft
                  ? AppStrings.DELETE_SEASON_CONFIRM_BODY_SUFFIX
                  : AppStrings.UNSCHEDULE_SEASON_CONFIRM_BODY_SUFFIX}
              </p>
            </div>
          </Modal>
        )}

        {/* ── End Season confirmation modal (only reachable from the Active
            season's End Season link). FE-2261: the production type-to-confirm
            gate (FE-2247) is behind `game_pass.end_season_confirm_v2`;
            flag-off (and while flags load) renders the legacy single-click
            confirmation. ─────────────────────────────────────────────── */}
        {isEndSeasonConfirmV2Enabled ? (
          <EndSeasonConfirmModal
            open={isEndSeasonOpen}
            onClose={handleCancelEndSeason}
            onConfirm={handleConfirmEndSeason}
            isPending={endSeasonMutation.isPending}
          />
        ) : (
          <Modal
            open={isEndSeasonOpen}
            onClose={handleCancelEndSeason}
            intent="destructive"
            size="sm"
            destroyOnHidden
            title={
              <span className={styles.endSeasonModalTitle}>
                {AppStrings.SEASON_DETAILS_END_SEASON_CONFIRM_TITLE}
              </span>
            }
            footer={
              <div className={styles.endSeasonModalFooter}>
                <Button
                  intent="neutral"
                  variant="outline"
                  onClick={handleCancelEndSeason}
                  data-testid="season-details-end-season-go-back"
                >
                  {AppStrings.SEASON_DETAILS_END_SEASON_CONFIRM_GO_BACK}
                </Button>
                <Button
                  intent="danger"
                  variant="outline"
                  style={{ color: "var(--COLOR-LOST_COLOR)" }}
                  onClick={() => handleConfirmEndSeason()}
                  disabled={endSeasonMutation.isPending}
                  loading={endSeasonMutation.isPending}
                  data-testid="season-details-end-season-confirm"
                >
                  {AppStrings.SEASON_DETAILS_END_SEASON_CONFIRM_CTA}
                </Button>
              </div>
            }
          >
            <div data-testid="season-details-end-season-modal">
              <p className={styles.endSeasonModalBody}>
                {`${AppStrings.SEASON_DETAILS_END_SEASON_CONFIRM_BODY_PREFIX} `}
                <span
                  className={styles.endSeasonModalName}
                >{`"${displayName}"`}</span>
                {AppStrings.SEASON_DETAILS_END_SEASON_CONFIRM_BODY_SUFFIX}
              </p>
            </div>
          </Modal>
        )}

        <Modal
          open={activationModalState.open}
          onClose={handleCloseActivateSeason}
          intent="destructive"
          size="sm"
          destroyOnHidden
          title={
            <span className={styles.endSeasonModalTitle}>
              {activationModalState.force
                ? AppStrings.ACTIVATE_SEASON_FORCE_MODAL_TITLE
                : AppStrings.ACTIVATE_SEASON_MODAL_TITLE}
            </span>
          }
          footer={
            <div className={styles.endSeasonModalFooter}>
              <Button
                intent="neutral"
                variant="outline"
                onClick={handleCloseActivateSeason}
                data-testid="season-details-activate-go-back"
              >
                {AppStrings.DELETE_SEASON_GO_BACK}
              </Button>
              <Button
                intent="danger"
                variant="outline"
                style={{ color: "var(--COLOR-PRIMARY, #ff6a2c)" }}
                onClick={handleConfirmActivateSeason}
                disabled={activateSeasonMutation.isPending}
                loading={activateSeasonMutation.isPending}
                data-testid="season-details-activate-confirm"
              >
                {activationModalState.force
                  ? AppStrings.ACTIVATE_SEASON_FORCE_CTA
                  : AppStrings.ACTIVATE_SEASON_CONFIRM_CTA}
              </Button>
            </div>
          }
        >
          <div data-testid="season-details-activate-modal">
            <p className={styles.endSeasonModalBody}>
              {activationModalState.force
                ? `${AppStrings.ACTIVATE_SEASON_FORCE_BODY_PREFIX} `
                : `${AppStrings.ACTIVATE_SEASON_CONFIRM_BODY_PREFIX} `}
              <span className={styles.endSeasonModalName}>{`"${displayName}"`}</span>
              {activationModalState.force
                ? AppStrings.ACTIVATE_SEASON_FORCE_BODY_SUFFIX
                : AppStrings.ACTIVATE_SEASON_CONFIRM_BODY_SUFFIX}
            </p>
            {activationModalState.force &&
              activationModalState.blockerMessage && (
                <p className={styles.endSeasonModalBody}>
                  {activationModalState.blockerMessage}
                </p>
              )}
          </div>
        </Modal>

        <Modal
          open={isCancelConfirmOpen}
          onClose={handleCloseCancelSeason}
          intent="destructive"
          size="sm"
          destroyOnHidden
          title={
            <span className={styles.endSeasonModalTitle}>
              {AppStrings.CANCEL_SEASON_MODAL_TITLE}
            </span>
          }
          footer={
            <div className={styles.endSeasonModalFooter}>
              <Button
                intent="neutral"
                variant="outline"
                onClick={handleCloseCancelSeason}
                data-testid="season-details-cancel-go-back"
              >
                {AppStrings.DELETE_SEASON_GO_BACK}
              </Button>
              <Button
                intent="danger"
                variant="outline"
                style={{ color: "var(--COLOR-LOST_COLOR)" }}
                onClick={handleConfirmCancelSeason}
                disabled={cancelSeasonMutation.isPending}
                loading={cancelSeasonMutation.isPending}
                data-testid="season-details-cancel-confirm"
              >
                {AppStrings.CANCEL_SEASON_CONFIRM_CTA}
              </Button>
            </div>
          }
        >
          <div data-testid="season-details-cancel-modal">
            <p className={styles.endSeasonModalBody}>
              {`${AppStrings.CANCEL_SEASON_CONFIRM_BODY_PREFIX} `}
              <span className={styles.endSeasonModalName}>{`"${displayName}"`}</span>
              {AppStrings.CANCEL_SEASON_CONFIRM_BODY_SUFFIX}
            </p>
          </div>
        </Modal>

        {/* ── Edit Season modal (editable mode only) ──────────────── */}
        {!isLocked && (
          <EditSeasonModal
            open={isEditGeneralOpen}
            seasonName={displayName}
            initialValues={editableGeneral}
            onSave={handleSaveEditGeneral}
            onSaveAsDraft={handleSaveEditGeneralAsDraft}
            onDelete={isDraft ? handleDeleteSeason : handleUnscheduleSeason}
            onClose={handleCloseEditGeneral}
            // For a SCHEDULED season the Save handler runs the overlap guard
            // (FE-1693), which needs the full seasons list — block Save while
            // that list is loading/errored so the check never silently passes
            // against empty data. Drafts skip the guard, so they aren't gated.
            isSaving={
              updateSeasonMutation.isPending ||
              (!isDraft && (seasonsQuery.isLoading || seasonsQuery.isError))
            }
            autoStartAnchor={autoStartAnchor}
            showDestructiveAction={Boolean(destructiveActionLabel)}
            destructiveActionLabel={destructiveActionLabel}
            destructiveActionTitle={
              isDraft
                ? AppStrings.DELETE_SEASON_MODAL_TITLE
                : AppStrings.UNSCHEDULE_SEASON_MODAL_TITLE
            }
            destructiveActionBodyPrefix={
              isDraft
                ? AppStrings.DELETE_SEASON_CONFIRM_BODY_PREFIX
                : AppStrings.UNSCHEDULE_SEASON_CONFIRM_BODY_PREFIX
            }
            destructiveActionBodySuffix={
              isDraft
                ? AppStrings.DELETE_SEASON_CONFIRM_BODY_SUFFIX
                : AppStrings.UNSCHEDULE_SEASON_CONFIRM_BODY_SUFFIX
            }
            // Delete copy is a self-contained sentence (no inline name);
            // unschedule copy still reads as `… unschedule "Name"?`.
            showSeasonName={!isDraft}
            destructiveActionConfirmCta={
              isDraft
                ? AppStrings.DELETE_SEASON_CONFIRM_CTA
                : AppStrings.UNSCHEDULE_SEASON_CONFIRM_CTA
            }
          />
        )}
        {/* ── View Season modal (locked / active mode only) ───────────
            Reuses the EditSeasonModal in its read-only mode so admins can
            review a locked or active season's General + Scheduling details
            via the eye icon — these seasons have no pencil-edit affordance.
            Save / Save-as-Draft / Delete and the destructive sub-modal are
            all suppressed by `readOnly`. */}
        {isLocked && (
          <EditSeasonModal
            open={isViewGeneralOpen}
            readOnly
            seasonName={displayName}
            initialValues={editableGeneral}
            onClose={handleCloseViewGeneral}
            // Required by the modal's contract but unreachable in read-only
            // mode — the footer (and the destructive sub-modal) are hidden.
            onSave={handleCloseViewGeneral}
            onSaveAsDraft={handleCloseViewGeneral}
            onDelete={handleCloseViewGeneral}
            showDestructiveAction={false}
          />
        )}
        {!isLocked && (
          <Modal
            open={isScheduleOpen}
            onClose={handleCloseSchedule}
            size="xl"
            destroyOnHidden
            title={<span>{`${AppStrings.SCHEDULE} "${displayName}"`}</span>}
            footer={
              <Button
                intent="primary"
                onClick={handleScheduleSeason}
                disabled={
                  !isScheduleValid(scheduleDraft) ||
                  updateSeasonMutation.isPending ||
                  scheduleSeasonMutation.isPending ||
                  seasonsQuery.isLoading ||
                  seasonsQuery.isError
                }
                loading={
                  updateSeasonMutation.isPending ||
                  scheduleSeasonMutation.isPending
                }
                data-testid="season-details-schedule-next"
              >
                {AppStrings.NEXT}
              </Button>
            }
          >
            <div data-testid="season-details-schedule-modal">
              <Schedule
                variant="embedded"
                values={scheduleDraft}
                onValuesChange={setScheduleDraft}
                testIdPrefix="season-details-schedule-modal"
                autoStartAnchor={autoStartAnchor}
              />
            </div>
          </Modal>
        )}
      </div>
    </>
  );
};

// ─── Locked view (Active / Up-Next) ──────────────────────────────────────
//
// Pure presentation — renders the sequential toggle in a read-only state and
// the per-challenge cards in the `locked` variant we added to ChallengeCard.
// The no-op callbacks below are passed because `ChallengeCardProps` requires
// them; the locked branch in `ChallengeCard` never invokes any of them.

type LockedChallengesViewProps = {
  sequential: boolean;
  challenges: ReadonlyArray<ChallengeDraft>;
};

const noop = (): void => undefined;

const LockedChallengesView: FC<LockedChallengesViewProps> = ({
  sequential,
  challenges,
}) => (
  <>
    <div
      className={styles.toggleRow}
      data-testid="season-details-sequential-toggle"
    >
      <ToggleSwitch value={sequential} onChange={noop} tone="info" disabled />
      <span>{AppStrings.USERS_MUST_COMPLETE_CHALLENGES_SEQUENTIALLY}</span>
    </div>

    <ul
      className={styles.lockedChallengeList}
      data-testid="season-details-challenge-list"
    >
      {challenges.map((challenge, index) => (
        <li
          key={challenge.id}
          className={styles.lockedChallengeItem}
          data-testid={`season-details-challenge-${challenge.id}`}
        >
          <ChallengeCard
            challenge={challenge}
            index={index + 1}
            mode="locked"
            onChange={noop}
            onSave={noop}
            onEdit={noop}
            onClose={noop}
            onDelete={noop}
            onDuplicate={noop}
          />
        </li>
      ))}
    </ul>
  </>
);

export default SeasonDetails;
